<template>
  <div class="home detail-actions-page">
    <div class="px-8 py-4">
      <!-- Header -->
      <div class="detail-page-header mb-4">
        <button type="button" class="btn btn-link text-decoration-none detail-page-back" @click="$router.go(-1)" style="padding: 10px 0">
          <AppIcon name="bi-arrow-left" class="bi bi-arrow-left me-2" />{{$t('back')}}
        </button>
        <div class="detail-page-title-row d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-3">
          <h3 class="detail-page-title mb-0">{{ $t('payment') }} #{{ payment.reference }}</h3>
          <div class="detail-page-actions">
            <button v-if="helper.checkAccess('edit_payments') != -1" @click="editRow()" style="padding: 5px;margin: 0;font-size: 12px !important;" class="btn btn-primary btn-xs detail-page-action--secondary">
              <AppIcon name="bi-pencil" class="bi bi-pencil" style="font-size:12px" />
              {{$t('edit')}}
            </button>
            <button v-if="helper.checkAccess('delete_payments') != -1 && payment.status !== 'canceled'" @click="cancelPayment()" style="padding: 5px;margin: 0;font-size: 12px !important;" class="btn btn-primary btn-xs detail-page-action--danger">
              <AppIcon name="bi-x-circle" class="bi bi-x-circle" style="font-size:12px" />
              {{$t('cancel_payment')}}
            </button>
          </div>
        </div>
      </div>

      <!-- Loading State -->
      <div v-if="loading" class="text-center" style="padding:2rem">
        <div class="spinner-border" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
      </div>

      <!-- Payment Info -->
      <div v-else-if="payment && payment.id">
        <!-- Tabs -->
        <ul class="nav nav-tabs detail-page-tabs mb-4">
          <li class="nav-item">
            <a class="nav-link" :class="{active: step === 'overview'}" @click="step = 'overview'">{{$t('overview')}}</a>
          </li>
          <li class="nav-item">
            <a class="nav-link" :class="{active: step === 'attachments'}" @click="step = 'attachments'">{{$t('attachments')}}</a>
          </li>
          <li class="nav-item">
            <a class="nav-link" :class="{active: step === 'history'}" @click="step = 'history'">{{$t('history')}}</a>
          </li>
        </ul>

        <!-- Tab Content -->
        <!-- Overview Tab -->
        <div v-if="step === 'overview'">
          <div class="row">
            <!-- Left Column: Payment Details -->
            <div class="col-md-8">
              <div class="card mb-4">
                <div class="card-header bg-white">
                  <h6 class="mb-0"><AppIcon name="bi-credit-card" class="bi bi-credit-card me-2" />{{$t('payment')}} {{$t('information')}}</h6>
                </div>
                <div class="card-body">
                  <div class="row">
                    <div class="col-md-6 mb-3">
                      <small class="text-muted d-block">{{$t('reference')}}</small>
                      <strong>{{payment.reference}}</strong>
                    </div>
                    <div class="col-md-6 mb-3">
                      <small class="text-muted d-block">{{$t('status')}}</small>
                      <span :class="getStatusClass(payment.status)">{{$t(payment.status)}}</span>
                    </div>
                    <div class="col-md-6 mb-3">
                      <small class="text-muted d-block">{{$t('date')}}</small>
                      <strong>{{formatDate(payment.date)}}</strong>
                    </div>
                    <div class="col-md-6 mb-3" v-if="payment.due_date">
                      <small class="text-muted d-block">{{$t('due_date')}}</small>
                      <strong>{{formatDate(payment.due_date)}}</strong>
                    </div>
                    <div class="col-md-6 mb-3">
                      <small class="text-muted d-block">{{$t('amount')}}</small>
                      <h5 class="text-primary mb-0">{{formatMoney(payment.amount)}}</h5>
                    </div>
                    <div class="col-md-6 mb-3">
                      <small class="text-muted d-block">{{$t('payment_method')}}</small>
                      <strong>{{$t(payment.payment_method)}}</strong>
                    </div>
                    <div class="col-md-6 mb-3" v-if="payment.type">
                      <small class="text-muted d-block">{{$t('type')}}</small>
                      <span :class="payment.type === 'revenue' ? 'badge bg-success' : 'badge bg-danger'">{{$t(payment.type)}}</span>
                    </div>
                    <div class="col-md-6 mb-3">
                      <small class="text-muted d-block">{{$t('category')}}</small>
                      <strong>{{getPaymentCategoryName(payment)}}</strong>
                    </div>
                    <div class="col-md-6 mb-3" v-if="payment.pm_reference">
                      <small class="text-muted d-block">{{$t('transaction_id')}}</small>
                      <strong>{{payment.pm_reference}}</strong>
                    </div>
                    <div class="col-md-6 mb-3">
                      <small class="text-muted d-block">{{$t('created_at')}}</small>
                      <strong>{{formatDate(payment.created_at)}}</strong>
                    </div>
                    <div class="col-md-6 mb-3">
                      <small class="text-muted d-block">{{$t('updated_at')}}</small>
                      <strong>{{formatDate(payment.updated_at)}}</strong>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Notes Card -->
              <div v-if="payment.notes" class="card">
                <div class="card-header bg-white">
                  <h6 class="mb-0"><AppIcon name="bi-sticky" class="bi bi-sticky me-2" />{{$t('notes')}}</h6>
                </div>
                <div class="card-body">
                  <p class="mb-0">{{ payment.notes }}</p>
                </div>
              </div>
            </div>

            <!-- Right Column: Related Info -->
            <div class="col-md-4">
              <div class="card">
                <div class="card-header bg-white">
                  <h6 class="mb-0"><AppIcon name="bi-link-45deg" class="bi bi-link-45deg me-2" />{{$t('related')}} {{$t('information')}}</h6>
                </div>
                <div class="card-body">
                  <div v-if="payment.client" class="mb-3">
                    <small class="text-muted d-block">{{$t('client')}}</small>
                    <strong>{{payment?.client?.name}}</strong>
                  </div>
                  <div v-if="payment.supplier" class="mb-3">
                    <small class="text-muted d-block">{{$t('supplier')}}</small>
                    <strong>{{payment?.supplier?.name}}</strong>
                  </div>
                  <div v-if="payment.project" class="mb-3">
                    <small class="text-muted d-block">{{$t('project')}}</small>
                    <strong>{{payment?.project?.name}}</strong>
                  </div>
                  <div v-if="payment.invoice" class="mb-3">
                    <small class="text-muted d-block">{{$t('invoice')}}</small>
                    <strong>{{payment?.invoice?.reference}}</strong>
                  </div>
                  <div v-if="payment.pos" class="mb-3">
                    <small class="text-muted d-block">{{$t('pos')}}</small>
                    <router-link :to="`/pos-transactions/${payment.pos.id}`"><strong>{{payment.pos.reference}}</strong></router-link>
                  </div>
                  <div v-if="payment.supplierInvoice">
                    <small class="text-muted d-block">{{$t('supplier_invoice')}}</small>
                    <strong>{{payment?.supplierInvoice?.reference}}</strong>
                  </div>
                  <div v-if="!payment.client && !payment.supplier && !payment.project && !payment.invoice && !payment.pos && !payment.supplierInvoice" class="text-muted">
                    {{$t('no_related_information')}}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Attachments Tab -->
        <div v-if="step === 'attachments'">
          <attachments :entity_id="payment.id" entity_type="transaction" />
        </div>

        <div v-if="step === 'history'">
          <document-history
            :history="history" 
            :loading="loadingHistory"
            :pagination="historyPagination"
            @pageChange="changeHistoryPage"
          />
        </div>

      </div>
    </div>


        <!-- Edit Modal -->
        <add-edit-payment-modal 
            v-if="editModalPaymentisOpen" 
            @modalClosed="editModalPaymentisOpen = false" 
            @refresh="getPayment($route.params.id)"
            modalId="edit-payment-modal-01" 
            :isModalOpen="editModalPaymentisOpen"  
            :data="payment" 
            action="update" 
        ></add-edit-payment-modal>
        <!-- Edit modal -->

        <!-- quote modal -->
        <invoice-template-modal 
            v-if="invoiceModalPaymentIsOpen" 
            @modalClosed="invoiceModalPaymentIsOpen = false" 
            modalId="invoice-payment-modal-01" 
            :isModalOpen="invoiceModalPaymentIsOpen"  
            :data="payment" 
            type="payment">
        </invoice-template-modal>
        <!-- quote modal -->
  </div>
</template>

<script>
import helper from '../../helper'
import moment from 'moment'
import attachments from '../../components/Attachment.vue'
import DocumentHistory from '../../components/DocumentHistory.vue'

import AddEditPaymentModal from '../../components/payment/AddEditPaymentModal'

import InvoiceTemplateModal from '../../components/invoiceLayout/InvoiceTemplateModal';


export default {
  name: 'PaymentDetails',
  components :{
    attachments,
    AddEditPaymentModal,
    InvoiceTemplateModal,
    DocumentHistory
  },
  data(){
    return {
        loading : false,
        payment : {},
        step : 'overview',
        total : 0,
        amount_paid : 0,
        total_purchases : 0,
        total_quote : 0,
        invoiceModalPaymentIsOpen : false,
        invoiceModalPayment : null,
        editModalPayment : null,
        editModalPaymentisOpen : false,
        createClientState : false,
        history : [],
        historyPagination : {
          current_page : 1,
          per_page : 5,
          total : 0
        },
        loadingHistory : false
    }
  },
  mounted(){
    window.mixpanel.track("payment_details");

    //check permissions
    if(helper.checkAccess('view_payments') == -1) return this.$router.push({path : '/'})
    if(helper.checkAccess('view_payments') == -2) return this.$router.push({path : '/subscribe'})
  },
  created(){
    if(!this.$route.params || !this.$route.params.id) return this.$route.push('/payments')
    this.getPayment(this.$route.params.id)
  },
  watch: {
    step(newValue) {
      if(newValue === 'history' && this.payment.id){
        this.getPaymentHistory(this.payment.id)
      }
    }
  },
  methods : {
        getPaymentCategoryName(payment) {
            if (payment?.category?.name) return payment.category.name
            if (payment?.invoice?.id || payment?.invoice_id || payment?.paymentable_type === 'invoice') return this.$t('invoice_payments')
            if (payment?.process?.id || payment?.process_id || payment?.paymentable_type === 'process') return this.$t('receipt_payments')
            if (payment?.pos?.id || payment?.pos_id || payment?.paymentable_type === 'pos') return this.$t('pos_payments')
            if (payment?.supplierInvoice?.id || payment?.supplier_invoice_id || payment?.paymentable_type === 'supplier_invoice') return this.$t('supplier_invoice_payments')
            if (payment?.purchase?.id || payment?.purchase_id || payment?.paymentable_type === 'purchase') return this.$t('purchase_payments')
            if (payment?.paymentable_type === 'subscription') return this.$t('subscription_payments')
            if (payment?.paymentable_type === 'project') return this.$t('project_payments')
            if (payment?.supplier?.id || payment?.supplier_id) return this.$t('supplier_payments')
            if (payment?.client?.id || payment?.client_id) return this.$t('client_payments')
            if (payment?.type === 'expense') return this.$t('other_expenses')
            if (payment?.type === 'revenue') return this.$t('other_income')
            return this.$t('uncategorized')
        },
        async getPayment(id){
            try{
                this.loading = true
                const response = await helper.customGet(`${process.env.VUE_APP_API}/transactions/${id}`)
                this.payment = response.data
                this.total = this.payment.total || this.payment.amount
                this.amount_paid = this.payment.amount_paid || this.payment.amount
                this.loading = false
                
            }catch(error){
                this.$router.push({path : '/payments'})
                this.loading = false
            }
        },
        async getPaymentHistory(id){
            try {
                this.loadingHistory = true
                const response = await helper.customGet(`${process.env.VUE_APP_API}/transactions/${id}/history`)
                this.history = response.data.data || response.data
                this.historyPagination = {
                    current_page : 1,
                    per_page : 5,
                    total : this.history.length
                }
                this.loadingHistory = false
            } catch (error) {
                this.loadingHistory = false
                alert('Error fetching history:', error)
            }
        },
        changeHistoryPage(page) {
            this.historyPagination.current_page = page;
            // The HistoryComponent will handle slicing the array if it's client-side pagination,
            // or we'd fetch the new page if it's server-side.
        },
        editRow(){
            this.editModalPaymentisOpen = true;
            this.payment.date = this.payment.date ? moment(this.payment.date).format('YYYY-MM-DD') : moment().format('YYYY-MM-DD');
        },
        ViewPayment(){
            this.invoiceModalPaymentIsOpen = true;
        },
        async cancelPayment(){
            this.$swal.fire({
            title: `${this.$t('are_you_sure')}?`,
            text : this.$t('you_cant_revert'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: this.$t('yes_cancel_it')
                }).then(async (result) => {
                if (result.isConfirmed) {
                    await helper.customPatch(`${process.env.VUE_APP_API}/transactions/${this.payment.id}`, { status: 'canceled' })
                    await this.getPayment(this.payment.id)
                }
            })
        },
        selectTriggred(){
            this.calculatePrices()
        }, 
        calculatePrices(){
            this.$forceUpdate();
            for(let i = 0 ; i < this.payment.products.length ; i++){
                this.payment.products[i].total = parseFloat(this.payment.products[i].price) * parseFloat(this.payment.products[i].quantity)

                this.payment.products[i].sub_total = Math.round(parseFloat(this.payment.products[i].total))
                if(this.payment.products[i].vat > 0){
                    this.payment.products[i].total += parseFloat((this.payment.products[i].total * (parseFloat(this.payment.products[i].vat) / 100)).toFixed(2))
                    this.payment.products[i].total = Math.round(this.payment.products[i].total)
                }
            }

            this.payment.total = this.payment.products && this.payment.products.length ? this.payment.products.map(o => o.total).reduce((a, c) => { return a + c }) : 0;
            this.totalHtPrice = this.payment.products && this.payment.products.length ? this.payment.products.map(o => o.sub_total).reduce((a, c) => { return a + c }) : 0;
        },
        parseTTctoHt(index,price){
            if(price && price > 0){
                this.payment.products[index].price = ((price / this.payment.products[index].quantity) / 1.2).toFixed(2)
                this.payment.products[index].sub_total = parseFloat(this.payment.products[index].price) * parseFloat(this.payment.products[index].quantity)
            }
        },
        emitAutoComplete(data){
            
            this.payment.products[data.index] = {
                ...{
                inventory_id : data.result.id,
                label : data.result.product.label,
                product_label : data.result.product.label,
                product_id : data.result.product.id,
                id : data.result.product.id,
                quantity : 0, 
                maxQty : data.result.quantity,
                price : data.result.price,
                purchase_price : data.result.purchase_price ? data.result.purchase_price : 0,
                vat :  data.result.vat ? data.result.vat : this.business.vat,
                total : data.result.total ? data.result.total : 0,
                sub_total : data.result.sub_total ? data.result.sub_total : 0,
            }}
            this.payment.products = [...this.payment.products]

            this.calculatePrices()
        },
        emitAutoCompleteSupplier(data){
            this.payment.supplier_id = data.result.id
            this.payment.supplier = data.result
        },
        getStatus(status){
            if(status == 'paid'){
                return 'badge bg-success rounded-pill small'
            }else if(status == 'partially_paid'){
                return 'badge bg-success rounded-pill small'
            }else if(status == 'pending'){
                return 'badge bg-primary rounded-pill small'
            }else if(status == 'refunded'){
                return 'badge bg-info rounded-pill small'
            }
        },
        getStatusClass(status) {
            const classes = {
                'paid': 'badge bg-success',
                'pending': 'badge bg-warning',
                'failed': 'badge bg-danger',
                'refunded': 'badge bg-info',
                'canceled': 'badge bg-secondary'
            }
            return classes[status] || 'badge bg-secondary'
        },
        openCreateSupplierModal(){
            this.createSuplierState = !this.createSuplierState
        },
    
        formatDate(date) { return helper.formatDate(date) },
        formatMoney(number) { return helper.formatMoney(number);}
    },
    computed : {
        business(){ return this.$store.getters.BUSINESS},
        helper(){ return helper}
    }
}
</script>

<style scoped>
    .table-borderless>:not(caption)>*>*{
        padding: 5px !important
    }
</style>
